import { skipToken } from "@reduxjs/toolkit/query";
import type { Integration } from "@repo/app-types/entities/integration";
import { countRecordingImportCustomerRows } from "@repo/app-types/entities/recordingImport";
import { PageHeader } from "@repo/ui/Navigation/pageHeader";
import { PageWidth } from "@repo/ui/Navigation/pageWidth";
import type { FC } from "react";
import { Navigate, Outlet, useOutletContext } from "react-router";
import { useRecordingImportStatusCountsQuery } from "../../../api/entities/recordingImport.js";
import { appPaths } from "../../../app/appPaths.js";
import {
  PageBody,
  TabbedPageLayout,
} from "../../../components/layout/pageLayout.js";
import {
  RouteTabs,
  type RouteTabItem,
} from "../../../components/layout/routeTabs.js";
import { useAppSelector } from "../../../hooks/hook.js";
import { selectNiceCxoneIntegration } from "../../integrations/integrationsSlice.js";
import { IntegrationLogo } from "./integrationLogo.js";

const TITLE = "NICE CXone";

/** What the Settings and Failed imports tabs share: the company's NICE CXone row. */
export interface NiceCxoneDetailOutletContext {
  integration: Integration;
}

export function useNiceCxoneDetailOutletContext(): NiceCxoneDetailOutletContext {
  return useOutletContext<NiceCxoneDetailOutletContext>();
}

/**
 * The "NICE CXone" detail page: a read-only **Settings** tab and a **Failed
 * imports** tab over the integration's `recording_import` rows. Alpharun sets
 * the integration up by script, so a company without one is redirected back
 * to the integrations overview (e.g. a stale deep link).
 */
export const NiceCxoneIntegrationDetailSection: FC = () => {
  const integration = useAppSelector(selectNiceCxoneIntegration);
  const importCounts = useRecordingImportStatusCountsQuery(
    integration ? { integrationId: integration.id } : skipToken,
  );

  if (!integration) {
    return <Navigate to={appPaths.settingsIntegrations.build()} replace />;
  }

  const importIssueCount = importCounts.data
    ? countRecordingImportCustomerRows(importCounts.data)
    : 0;
  const tabs: RouteTabItem[] = [
    { label: "Settings", to: "." },
    {
      label:
        importIssueCount > 0
          ? `Failed imports (${importIssueCount.toLocaleString()})`
          : "Failed imports",
      to: "failed-imports",
    },
  ];

  const context: NiceCxoneDetailOutletContext = { integration };

  return (
    <TabbedPageLayout
      header={
        <PageHeader
          width={PageWidth.Wide}
          title={
            <span className="flex min-w-0 items-center gap-2">
              <IntegrationLogo provider={integration.provider} />
              <span className="truncate">{TITLE}</span>
            </span>
          }
          tabs={<RouteTabs ariaLabel="NICE CXone sections" tabs={tabs} />}
        />
      }
    >
      <PageBody width={PageWidth.Wide}>
        <Outlet context={context} />
      </PageBody>
    </TabbedPageLayout>
  );
};
